import React, { type FC, ReactNode, useMemo } from 'react';
import { TablePaginationConfig, type ColumnsType } from 'antd/es/table';
import { Link, Space, Text } from '@g2u/ui-kit';
import { Table, Spin, Empty } from 'antd';

import { type Technician } from 'types';
import styles from './technician-list.module.scss';

export namespace TechnicianList {
  export type Action =
    | ((record: Technician) => ReactNode)
    | {
        title: string;
        action: (record: Technician) => void;
      };

  type PaginationMetadata = {
    total: number;
    current_page: number;
    pageSize: number;
    onChange: TablePaginationConfig['onChange'];
  };

  export type Props = {
    technicians?: Technician[];
    actions?: Action[];
    paginationMetadata?: PaginationMetadata;
    isLoading?: boolean;
  };

  const columnsDefinition: ColumnsType<Technician> = [
    {
      title: 'ID',
      dataIndex: 'ID',
      key: 'ID',
      width: 50,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => <>{record.name}</>,
      width: 150,
    },
    {
      title: 'Contact',
      dataIndex: 'contact',
      key: 'contact',
      render: (_, record) => (
        <div>{record.contact?.map((number: string, idx: number) => <div key={idx}>{number}</div>)}</div>
      ),
      width: 120,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 180,
    },
    {
      title: 'Address',
      dataIndex: ['address', 'full_address'],
      key: 'address',
      render: (_, record) => <>{record.address?.full_address || ''}</>,
    },
  ];

  const actionsRenderer = (actions: Action[]) =>
    function Actions(_: Action, record: Technician) {
      return (
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
          {actions?.map((item, i) =>
            typeof item === 'function' ? (
              <React.Fragment key={i}>{item(record)}</React.Fragment>
            ) : (
              <Link.$
                key={item.title}
                onClick={(e) => {
                  e.preventDefault();
                  item.action(record);
                }}
                href="#"
                className={styles.techniciansList__action}
              >
                {item.title}
              </Link.$>
            ),
          )}
        </Space.$>
      );
    };

  const defaultPageSize = 5;

  export const $: FC<Props> = ({ technicians, actions = [], paginationMetadata: meta, isLoading }) => {
    const columns = useMemo(() => {
      if (actions.length === 0) return columnsDefinition;

      return (columnsDefinition as ColumnsType<Technician>).concat({
        key: 'actions',
        title: 'Actions',
        render: actionsRenderer(actions),
        width: 120,
      });
    }, [actions]);

    if (isLoading) {
      return <Spin data-testid="spinner" />;
    }

    if (technicians?.length === 0) {
      return (
        <Empty
          style={{ marginBottom: 20 }}
          imageStyle={{ height: 60 }}
          description={<Text.$ type="secondary">Sorry, there are no matching results.</Text.$>}
        />
      );
    }

    const paginationProps: TablePaginationConfig | undefined =
      meta && meta.total > (meta.pageSize || defaultPageSize)
        ? {
            rootClassName: styles.techniciansList__pagination,
            total: meta.total,
            current: meta.current_page,
            showTotal: () => <>Total {meta.total} items</>,
            showSizeChanger: true,
            defaultPageSize: meta.pageSize || defaultPageSize,
            pageSizeOptions: [defaultPageSize, 10, 20, 50, 100],
            onChange: typeof meta.onChange === 'function' ? meta.onChange : undefined,
          }
        : undefined;

    return <Table pagination={paginationProps} rowKey="ID" columns={columns} dataSource={technicians} />;
  };
}
